import { NgIf } from '@angular/common';
import {
  Component,
  ElementRef,
  QueryList,
  ViewChildren,
  AfterViewInit,
  Input,
  EventEmitter,
  Output,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../../../services/API/auth.service/auth.service';
import { Router } from '@angular/router';
import { TokenService } from '../../../../services/state-management/token.service/token.service';
import { response } from 'express';
import { LOCAL_STORAGE } from '../../../../models/constant/constants';

@Component({
  selector: 'app-otp-verification',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, NgIf],
  templateUrl: './otp-verification.component.html',
  styleUrls: ['./otp-verification.component.scss'],
})
export class OtpVerificationComponent implements AfterViewInit {
  @Input() email?: string;
  @Input() phoneNumber?: number | string;
  @Input() btnText!: string;
  @Input() inputValue?: any;
  @Input() isLoginOrSignup?: boolean;
  @Input() inputType?: any = '';
  @Output() dataEvent = new EventEmitter<boolean>();
  @Input() isStaySignedIn?:boolean =true ;
  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef>;
  otp: string[] = ['', '', '', '', ''];
  otpForm: FormGroup;

  constructor(
    private fb: FormBuilder,
    private toastr: ToastrService,
    private authService: AuthService,
    private tokenService: TokenService,
    private router: Router
  ) {
    this.otpForm = this.fb.group({
      otp: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
      staySignedIn: [true],
    });
  }

  sendData() {
    const data = false;
    this.dataEvent.emit(data); // Emit data to the parent
  }

  // Lifecycle hook to ensure ViewChildren is populated
  ngAfterViewInit(): void {
    if (!this.otpInputs) {
      console.error('OTP input elements are not initialized');
    }
  }


  // Handle input and move focus to the next field
  moveToNext(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, ''); // Allow only digits
  

    // Set the value in the OTP array
    this.otp[index] = value;
    input.value = value;
  
    // Move focus to the next input if value is entered
    if (value && index < this.otp.length - 1) {
      const nextInput = this.otpInputs.toArray()[index + 1]?.nativeElement;
      if (nextInput) {
        nextInput.focus();
      }
    }
  }

  // Handle backspace and move focus to the previous field
  moveToPrevious(event: KeyboardEvent, index: number): void {
    const input = event.target as HTMLInputElement;

    if (event.key === 'Backspace' && input.value === '' && index > 0) {
      const prevInput = this.otpInputs.toArray()[index - 1]?.nativeElement;
      if (prevInput) {
        prevInput.focus();
        this.otp[index - 1] = ''; // Clear the previous field
      } else {
        console.error('Previous input element is not available');
      }
    }
  }

  // Handle OTP paste event
  onPaste(event: ClipboardEvent): void {
    const clipboardData = event.clipboardData;
    const pastedData = clipboardData?.getData('Text') || '';

    // Fill the OTP fields with the pasted data
    if (pastedData.length === this.otp.length) {
      this.otp = pastedData.split('');
      this.otpInputs.forEach((input, i) => {
        if (input.nativeElement) {
          input.nativeElement.value = this.otp[i];
        }
      });
    }

    event.preventDefault(); // Prevent the default paste behavior
  }

  // Handle OTP submission
  onSubmit(): void {
    const otpCode = this.otp.join('');
    const identifier = this.email || this.phoneNumber;
    const staySignedIn = this.otpForm.get('staySignedIn')?.value;

    if (otpCode.length === this.otp.length) {
      const loginFilter = this.email ? 'EMAIL' : 'PHONE';

      if (this.isLoginOrSignup) {
        this.authService.verifySignUpOtp(loginFilter, identifier!, otpCode,staySignedIn).subscribe(
          (response) => {
            if(staySignedIn){
              const { authToken, ...userData } = response.data;
              this.tokenService.setToken(LOCAL_STORAGE.STAY_LOGGED_IN, staySignedIn, true);
              this.tokenService.setToken(LOCAL_STORAGE.STAY_SIGNED_IN, staySignedIn, staySignedIn);
              this.tokenService.setToken(LOCAL_STORAGE.TOKEN, authToken, staySignedIn);
              this.tokenService.setToken(LOCAL_STORAGE.USER_DETAILS, JSON.stringify(userData), staySignedIn);

              this.toastr.success('Login successfully');
              this.router.navigateByUrl('/gallery');
            }else{
              this.toastr.success('OTP Verified', 'Success');
              this.router.navigateByUrl('/auth/login');
            }
          },
          (error:any) => {
            this.toastr.error('Invalid OTP', 'Error');
            console.error('API Error:', error);
          }
        );
      } else {
        // login
        this.authService
          .verifySignInOtp(loginFilter, this.email!, otpCode)
          .subscribe(
            (response) => {
              this.toastr.success(response.message);
              const { authToken, ...userData } = response.data;
              this.tokenService.setToken(LOCAL_STORAGE.STAY_LOGGED_IN, staySignedIn, true);
              this.tokenService.setToken(LOCAL_STORAGE.STAY_SIGNED_IN, staySignedIn, staySignedIn);
              this.tokenService.setToken(LOCAL_STORAGE.TOKEN, authToken, staySignedIn);
              this.tokenService.setToken(LOCAL_STORAGE.USER_DETAILS, JSON.stringify(userData), staySignedIn);
              this.router.navigateByUrl('/gallery');
            },
            (error) => {
              console.error('API Error:', error);
            }
          );
      }
    } else {
      this.toastr.error('Please enter a valid OTP.');
    }
  }

  // Handle OTP resend
  onResendOtp(): void {
    // Reset the OTP form control
    this.otpForm.get('otp')?.reset();
    // Also clear the OTP input array for UI consistency
    this.otp = ['', '', '', '', ''];

    // Reset the UI input fields
    this.otpInputs.forEach((input) => {
      input.nativeElement.value = '';
    });
    const email = this.email;
    const loginFilter = this.email ? 'EMAIL' : 'PHONE';

    this.authService.resendOtp(this.email!, loginFilter).subscribe({
      next: (response) => {
        this.toastr.info(response.message);
      },
      error: (error) => {
        this.toastr.error(error.message);
      },
    });
  }
}
